// Every message the lobby sends to its embedding operator page goes through postToParent, which
// addresses it to a resolved origin and never to "*" (PLAY-5315, Sonar S2819). The union below is
// the operator-facing contract: types and payloads must not change without telling operators.

export type ParentMessage =
  | { type: "CASHIER_REQUESTED" }
  | { type: "VERSION_UPDATED"; payload: { version: string; build: string } }
  | {
      type: "SWITCH_DEVICE_REQUESTED";
      payload: { location: { pathname?: string; search?: string }; gameId: string | null };
    }
  | { type: `${string}_PLAY_SELECTED` }
  | { type: "SESSION_EXPIRED" }
  | { type: "GAME_CLOSED"; payload: { location: { pathname?: string; search?: string } } }
  | {
      type: "GAME_LAUNCHED";
      payload: { location?: { pathname?: string; search?: string }; gameId?: string; playType?: string };
    }
  | { type: "FULLSCREEN_ENTERED" }
  | { type: "FULLSCREEN_EXITED" }
  | { type: "LANGUAGE_CHANGED"; payload: { locale: string } }
  | { type: "PROFILE_UPDATE_REQUESTED" }
  | { type: "LOBBY_EXITED" };

export const PARENT_ORIGIN_STORAGE_KEY = "qt.parentOrigin";

// `undefined` = not resolved yet this page load; `null` = resolved to "no trustworthy origin".
let resolvedOrigin: string | null | undefined;
let warned = false;

function toOrigin(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const { origin } = new URL(value);
    return origin && origin !== "null" ? origin : null;
  } catch {
    return null;
  }
}

function readPersisted(): string | null {
  try {
    return toOrigin(globalThis.sessionStorage.getItem(PARENT_ORIGIN_STORAGE_KEY));
  } catch {
    return null;
  }
}

function persist(origin: string) {
  try {
    globalThis.sessionStorage.setItem(PARENT_ORIGIN_STORAGE_KEY, origin);
  } catch {
    // Storage can be blocked or partitioned in third-party iframes: resolution then works per load.
  }
}

function isAllowed(origin: string): boolean {
  const allowList = (globalThis as typeof globalThis & Window).APP_PARENT_ORIGINS;
  return !Array.isArray(allowList) || allowList.length === 0 || allowList.includes(origin);
}

function warnOnce(text: string) {
  if (warned) return;
  warned = true;
  console.warn(`[parentMessaging] ${text}`);
}

function resolveFramedOrigin(): string | null {
  const ownOrigin = globalThis.location.origin;
  const ancestor = toOrigin(globalThis.location.ancestorOrigins?.[0]);
  if (ancestor) return ancestor;

  // A same-origin referrer means the lobby reloaded itself (setLocale, exit), not that the
  // operator is the lobby, so it falls through to the value persisted on first load.
  const referrer = toOrigin(globalThis.document.referrer);
  if (referrer && referrer !== ownOrigin) return referrer;

  return readPersisted();
}

/** The origin lobby events are posted to, or null when none can be trusted. Memoised per load. */
export function resolveParentOrigin(): string | null {
  if (resolvedOrigin !== undefined) return resolvedOrigin;

  if (globalThis.parent === globalThis.window) {
    resolvedOrigin = globalThis.location.origin;
    return resolvedOrigin;
  }

  const candidate = resolveFramedOrigin();
  if (candidate && !isAllowed(candidate)) {
    warnOnce(`parent origin ${candidate} is not in APP_PARENT_ORIGINS; messages not sent`);
    resolvedOrigin = null;
    return resolvedOrigin;
  }

  if (candidate) persist(candidate);
  resolvedOrigin = candidate;
  return resolvedOrigin;
}

/** Posts `message` unchanged to the resolved parent origin; sends nothing when it is unresolved. */
export function postToParent(message: ParentMessage): void {
  const targetOrigin = resolveParentOrigin();
  if (!targetOrigin) {
    warnOnce(`parent origin unresolved; message ${message.type} not sent`);
    return;
  }
  globalThis.parent.postMessage(message, targetOrigin);
}
